import { Chip, Pagination, Spacer } from "@heroui/react";
import { Avatar } from "components/index";
import { Email, Phone } from "assets/icons";
import React, { useEffect, useState } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth.ts";
import { API_URL } from "constants/index.ts";
import { useSession } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { ContactSkeleton } from "shared/skeletons/contact-card.skeleton";
import { useWindowResize } from "hooks/useWindowResize.ts";

export interface ContactCardProps {
  image?: string;
  name?: string;
  company?: string;
  phone?: string;
  email?: string;
  subcategory?: any;
}

interface ContactProps {
  contact: any;
  onContactSelect?: (contact: any) => void;
}

interface CompanyProps {
  company: any;
}

export const Contact = ({ contact }: ContactProps) => {
  return (
    <Link
      className="flex shrink grow basis-auto duration-250 will-change-transform hover:scale-[1.0124]"
      to={`/contact/${contact.id}`}
      state={contact}
    >
      <div className="flex w-full items-center gap-3 overflow-hidden rounded-lg bg-white p-2.5 shadow-md sm:items-start sm:p-4">
        <div className="shrink-0">
          <Avatar sizing="h-10 w-10" src={contact?.picture ?? undefined} />
        </div>
        <div className="flex w-full items-center justify-between gap-1 overflow-hidden sm:flex-col sm:items-start sm:gap-2.5">
          <div className="flex flex-col gap-1">
            <p className="w-full truncate text-base font-medium text-neutral-primary sm:w-40 lg:w-60">
              {contact.firstName} {contact.lastName}
            </p>
            {contact.company && (
              <Link to={`/company/${contact.company.id}`}>
                <span className="w-full truncate text-sm text-neutral-secondary-mid sm:w-40 lg:w-60">
                  {contact.company.name}
                </span>
              </Link>
            )}
            {contact.tags &&
              contact.tags.map((tag: string, idx: number) => (
                <Chip
                  className="gap-1"
                  classNames={{
                    base: "h-fit px-2 bg-white border border-neutral-secondary-mid text-neutral-secondary-mid",
                    content: "px-0 font-medium",
                  }}
                  key={idx}
                >
                  {tag}
                </Chip>
              ))}
          </div>
          <ul className="flex gap-2 sm:hidden sm:flex-col sm:gap-1">
            {contact.phone_mobile && (
              <a
                href={"tel:" + contact.phone_mobile}
                onClick={(e) => e.stopPropagation()}
              >
                <li className="flex items-center gap-2 rounded-full border border-neutral-primary/20 p-1.5 font-medium text-moss-primary">
                  <Phone className="h-5 w-5 sm:h-[17px] sm:w-[17px]" />
                  <p className="hidden w-full truncate sm:inline sm:w-40 lg:w-60">
                    {contact.phone_mobile}
                  </p>
                </li>
              </a>
            )}
            {contact.email && (
              <a
                href={"mailto:" + contact.email}
                onClick={(e) => e.stopPropagation()}
              >
                <li className="flex items-center gap-2 rounded-full border border-neutral-primary/20 p-1.5 font-medium text-moss-primary">
                  <Email className="h-5 w-5 sm:h-[17px] sm:w-[17px]" />
                  <p className="hidden w-full truncate sm:inline sm:w-40 lg:w-60">
                    {contact.email}
                  </p>
                </li>
              </a>
            )}
          </ul>
          <ul className="hidden gap-2 sm:flex sm:flex-col sm:gap-1">
            {contact.phone_mobile && (
              <a
                href={"tel:" + contact.phone_mobile}
                onClick={(e) => e.stopPropagation()}
              >
                <li className="flex items-center gap-2 rounded-full border border-neutral-primary/20 p-1.5 font-medium text-moss-primary sm:rounded-none sm:border-none sm:p-0">
                  <Phone className="h-5 w-5 sm:h-[17px] sm:w-[17px]" />
                  <p className="hidden w-full truncate sm:inline sm:w-40 lg:w-60">
                    {contact.phone_mobile}
                  </p>
                </li>
              </a>
            )}
            {contact.email && (
              <a
                href={"mailto:" + contact.email}
                onClick={(e) => e.stopPropagation()}
              >
                <li className="flex items-center gap-2 rounded-full border border-neutral-primary/20 p-1.5 font-medium text-moss-primary sm:rounded-none sm:border-none sm:p-0">
                  <Email className="h-5 w-5 sm:h-[17px] sm:w-[17px]" />
                  <p className="hidden w-full truncate sm:inline sm:w-40 lg:w-60">
                    {contact.email}
                  </p>
                </li>
              </a>
            )}
          </ul>
        </div>
      </div>
    </Link>
  );
};

export const Company = ({ company }: CompanyProps) => {
  return (
    <Link
      className="flex shrink grow basis-auto duration-250 will-change-transform hover:scale-[1.0124]"
      to={`/company/${company.id}`}
      state={company}
    >
      <div className="flex w-full items-center justify-between gap-3 overflow-hidden rounded-lg border-2 border-brick-primary bg-white p-2.5 shadow-md sm:items-start sm:justify-normal sm:p-4">
        <div className="shrink-0">
          <Avatar sizing="h-10 w-10" src={company.image} />
        </div>
        <div className="flex w-full items-center gap-1 overflow-hidden sm:flex-col sm:items-start sm:gap-2.5">
          <div className="flex grow flex-col gap-1">
            <p className="w-full truncate text-base font-medium text-neutral-primary sm:w-40 lg:w-60">
              {company.name}
            </p>
            {company.address && (
              <p className="w-full truncate text-sm text-neutral-secondary-mid sm:w-40 lg:w-60">
                {company?.address?.street} {company?.address?.city}
              </p>
            )}
            {company.tags &&
              company.tags.map((tag: string, idx: number) => (
                <div>
                  <Chip
                    className="gap-1"
                    classNames={{
                      base: "h-fit px-2 bg-white border border-neutral-secondary-mid text-neutral-secondary-mid",
                      content: "px-0 font-medium",
                    }}
                    key={idx}
                  >
                    {tag}
                  </Chip>
                </div>
              ))}
          </div>
          <ul className="flex gap-2 sm:hidden sm:flex-col sm:gap-1">
            {company.phone_direct && (
              <a
                href={"tel:" + company.phone_direct}
                onClick={(e) => e.stopPropagation()}
              >
                <li className="flex items-center gap-2 rounded-full border border-neutral-primary/20 p-1.5 font-medium text-moss-primary">
                  <Phone className="h-5 w-5 sm:h-[17px] sm:w-[17px]" />
                  <p className="hidden w-full truncate sm:inline sm:w-40 lg:w-60">
                    {company.phone_direct}
                  </p>
                </li>
              </a>
            )}
            {company.email && (
              <a
                href={"mailto:" + company.email}
                onClick={(e) => e.stopPropagation()}
              >
                <li className="flex items-center gap-2 rounded-full border border-neutral-primary/20 p-1.5 font-medium text-moss-primary">
                  <Email className="h-5 w-5 sm:h-[17px] sm:w-[17px]" />
                  <p className="hidden w-full truncate sm:inline sm:w-40 lg:w-60">
                    {company.email}
                  </p>
                </li>
              </a>
            )}
          </ul>
          <ul className="hidden gap-2 sm:flex sm:flex-col sm:gap-1">
            {company.phone_direct && (
              <a
                href={"tel:" + company.phone_direct}
                onClick={(e) => e.stopPropagation()}
              >
                <li className="flex items-center gap-2 rounded-full border border-neutral-primary/20 p-1.5 font-medium text-moss-primary sm:rounded-none sm:border-none sm:p-0">
                  <Phone className="h-5 w-5 sm:h-[17px] sm:w-[17px]" />
                  <p className="hidden w-full truncate sm:inline sm:w-40 lg:w-60">
                    {company.phone_direct}
                  </p>
                </li>
              </a>
            )}
            {company.email && (
              <a
                href={"mailto:" + company.email}
                onClick={(e) => e.stopPropagation()}
              >
                <li className="flex items-center gap-2 rounded-full border border-neutral-primary/20 p-1.5 font-medium text-moss-primary sm:rounded-none sm:border-none sm:p-0">
                  <Email className="h-5 w-5 sm:h-[17px] sm:w-[17px]" />
                  <p className="hidden w-full truncate sm:inline sm:w-40 lg:w-60">
                    {company.email}
                  </p>
                </li>
              </a>
            )}
          </ul>
        </div>
      </div>
    </Link>
  );
};

export const ContactCard = ({ subcategory }: ContactCardProps) => {
  const session = useSession();
  const [allItems, setAllItems] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastContactId, setLastContactId] = useState<number | null>(null);
  const [lastCompanyId, setLastCompanyId] = useState<number | null>(null);
  const { x } = useWindowResize();
  const [isLoading, setIsLoading] = useState(false);
  const [nextLastContactId, setNextLastContactId] = useState<number | null>(
    null,
  );
  const [nextLastCompanyId, setNextLastCompanyId] = useState<number | null>(
    null,
  );
  const [prevLastContactId] = useState<number | null>(null);
  const [prevLastCompanyId] = useState<number | null>(null);

  const itemsPerPage = 100; // Adjust this if needed
  const displayedItemsStart = (currentPage - 1) * itemsPerPage + 1;
  const displayedItemsEnd = Math.min(
    displayedItemsStart + itemsPerPage - 1,
    totalItems,
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          ...(subcategory ? { subCategory: subcategory } : {}),
          ...(lastContactId ? { lastContactId: lastContactId.toString() } : {}),
          ...(lastCompanyId ? { lastCompanyId: lastCompanyId.toString() } : {}),
          limit: itemsPerPage.toString(),
        });

        const url = `${API_URL}/contact/all?${queryParams}`;
        const response = await fetchWithAuth(
          url,
          { method: "GET" },
          session.session,
        );
        const data = await response.json();

        setAllItems(data.items);
        setTotalItems(data.totalItems);
        setNextLastContactId(data.lastContactId);
        setNextLastCompanyId(data.lastCompanyId);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [subcategory, currentPage, session.session, lastContactId, lastCompanyId]);

  const handlePageChange = (newPage: number) => {
    if (newPage > currentPage) {
      // Going forward
      setLastContactId(nextLastContactId);
      setLastCompanyId(nextLastCompanyId);
    } else {
      // Going backward
      setLastContactId(prevLastContactId);
      setLastCompanyId(prevLastCompanyId);
    }
    setCurrentPage(newPage);
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
        {isLoading &&
          [...Array(60)].map((_, idx) => (
            <React.Fragment key={idx}>
              <ContactSkeleton />
            </React.Fragment>
          ))}
        {!isLoading && allItems.length === 0 && (
          <p>Er zijn geen contacten of bedrijven gevonden {subcategory}</p>
        )}
        {allItems.length > 0 &&
          allItems.map((item) => {
            console.log("ITEM", item);
            return item.entityType === "contact" ? (
              <Contact key={`${item.id}+${item.odoo_id}`} contact={item} />
            ) : (
              <Company key={`${item.id}+${item.odoo_id}`} company={item} />
            );
          })}
      </div>
      <div className="mt-4 sm:mt-0 sm:flex sm:items-center sm:justify-between">
        <Spacer y={4} />
        <div className="flex flex-wrap items-center">
          <span className="mb-2 ml-2 mr-4 mt-2">
            {displayedItemsStart} - {displayedItemsEnd} van {totalItems} items
          </span>
          <div className="">
            {totalItems && (
              <Pagination
                total={Math.ceil(totalItems / itemsPerPage)}
                initialPage={1}
                size={x < 640 ? "sm" : "md"}
                onChange={handlePageChange}
                className=""
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
